import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '../../../store/store';
import { useBotContext } from '../store/BotContext';
import { useChatContext } from '../../bot-core/store/ChatContext';
import { createSession, loadSession, updateSessionContext, archiveSession, getCheckoutPayload } from '../api/botApi';
import { BotNewSessionLimitError } from '../utils/botSessionLimit';
import { ItineraryItem, CheckoutPayload } from '../types/bot.types';
import { UIMessage } from '../../bot-core/types/chat.types';
import { PriceView } from '../../../hooks/useGetGrossPrice';

/** Visible tab strip entry — one per session the user has open in this
 *  widget. The full session state lives on the server; tabs are local
 *  pointers. Scaffold for the multi-session UX requested by the user; only
 *  the active tab's chat is "live" at a time. */
export interface BotTab {
  id: string;       // session id
  title?: string;   // session title from BE (falls back to placeholder in UI)
}

function sessionKey(tenantId?: number | string, websiteId?: number | string) {
  return `sky360_bot_session_${tenantId ?? 'x'}_${websiteId ?? 'x'}`;
}
function tabsKey(tenantId?: number | string, websiteId?: number | string) {
  return `sky360_bot_tabs_${tenantId ?? 'x'}_${websiteId ?? 'x'}`;
}

interface PersistedTabs {
  tabs: BotTab[];
  activeId: string | null;
}

function readPersistedTabs(key: string): PersistedTabs {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return { tabs: [], activeId: null };
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.tabs)) return { tabs: [], activeId: null };
    return { tabs: parsed.tabs, activeId: parsed.activeId ?? null };
  } catch {
    return { tabs: [], activeId: null };
  }
}

/** Events365 lang — same shape B2C sends on /event/search (en_us, he_il, …). */
function toBotSearchLanguage(currentLang?: string): string {
  return (currentLang ?? 'en_US').toLowerCase();
}

export interface UseBotSessionOptions {
  /**
   * Called with a session the user is walking away from — closing its tab,
   * switching to another one, or starting a new chat — while a turn may still
   * be running for it. The widget wires this to the socket's `cancelTurn` so
   * the backend stops generating (and billing for) a reply nobody will see.
   * Purely advisory: the reply is also filtered client-side by session id.
   */
  onAbandonSession?: (sessionId: string) => void;
}

export function useBotSession(options: UseBotSessionOptions = {}) {
  const { botState, botDispatch } = useBotContext();
  const { dispatch: chatDispatch } = useChatContext();

  // Held in a ref so the lifecycle callbacks below don't need it in their dep
  // arrays — the widget assigns it after useChatSocket has run.
  const onAbandonSessionRef = useRef(options.onAbandonSession);
  onAbandonSessionRef.current = options.onAbandonSession;
  const abandon = useCallback((sessionId: string | null | undefined) => {
    if (sessionId) onAbandonSessionRef.current?.(sessionId);
  }, []);

  // The session the live chat is currently showing, readable from callbacks
  // without pulling it into their dep arrays.
  const liveSessionRef = useRef<string | null>(botState.sessionId);
  liveSessionRef.current = botState.sessionId;

  const membershipUser = useSelector((s: RootState) => s.config.membershipUser) as any;
  const currentLang = useSelector((s: RootState) => s.config.currentLang) as string | undefined;
  // Agent-only display toggle; undefined for every B2C visitor. See the sync
  // effect below for why the backend needs to be told.
  const priceView = useSelector(
    (s: RootState) => s.config.agentInfo?.agent?.price_view,
  ) as PriceView | undefined;

  const tenantId = Number(process.env.REACT_APP_TENANT_ID) || undefined;
  const websiteId = Number(process.env.REACT_APP_WEBSITE_ID) || undefined;
  const sKey = sessionKey(tenantId, websiteId);
  const tKey = tabsKey(tenantId, websiteId);

  const [tabs, setTabs] = useState<BotTab[]>(() => readPersistedTabs(tKey).tabs);
  const [activeTabId, setActiveTabId] = useState<string | null>(
    () => readPersistedTabs(tKey).activeId,
  );

  // In-flight guard for lazy session creation — prevents a rapid double-send
  // (before the first session exists) from creating two sessions.
  const creatingRef = useRef<Promise<string | null> | null>(null);

  // Persist tabs on any change so the strip survives reload.
  useEffect(() => {
    try {
      localStorage.setItem(
        tKey,
        JSON.stringify({ tabs, activeId: activeTabId } as PersistedTabs),
      );
    } catch {
      // localStorage quota errors are non-fatal — the strip will rebuild
      // on next session.
    }
  }, [tabs, activeTabId, tKey]);

  // Keep session search.language in sync when the user switches site language.
  useEffect(() => {
    const sessionId = botState.sessionId;
    if (!sessionId || !currentLang) return;
    const lang = toBotSearchLanguage(currentLang);
    updateSessionContext(sessionId, {
      search: { language: lang },
    }).catch((e) => console.error('[BotSession] sync language', e));
  }, [currentLang, botState.sessionId]);

  /**
   * Keep the session's price view in sync with the agent's toggle.
   *
   * The backend cannot read this: the effective value is
   * `localStorage['price_view']` overriding the agent record, and the toggle
   * lives in AgentUserBanner. Until it was sent, the bot's flight listing quoted
   * net prices while the FlightCard2 rendered beside it applied the commission
   * markup — so a gross-view agent was shown two prices for the same flight and
   * neither the chat nor the card said which was which.
   *
   * Same shape as the language sync above, and re-sent on toggle so a mid-chat
   * flip re-prices the next search.
   */
  useEffect(() => {
    const sessionId = botState.sessionId;
    if (!sessionId) return;
    updateSessionContext(sessionId, {
      price_view: priceView === PriceView.Gross ? 'gross' : 'net',
    }).catch((e) => console.error('[BotSession] sync price view', e));
  }, [priceView, botState.sessionId]);

  const refreshSelections = useCallback(async (sessionId: string) => {
    try {
      const session = await loadSession(sessionId);
      const raw = session.context?.cart ?? session.context?.selections ?? [];
      const items: ItineraryItem[] = raw.map((s) => ({
        selection_id: ('cart_item_id' in s ? s.cart_item_id : s.selection_id) as string,
        product_type: s.product_type,
        display_name: s.display_name,
        display_summary: s.display_summary,
        price: s.price,
        currency: s.currency,
      }));
      const total = items.reduce((sum, i) => sum + Number(i?.price), 0);
      const currency = items[0]?.currency ?? 'USD';
      botDispatch({ type: 'SET_SELECTIONS', selections: items, totalPrice: total, currency });
    } catch (e) {
      console.error('[BotSession] refreshSelections', e);
    }
  }, [botDispatch]);

  const applyCheckoutPayload = useCallback(
    (payload: CheckoutPayload | null | undefined, expiresAt?: string | null) => {
      if (!payload?.itinerary_display?.length) {
        botDispatch({ type: 'CLEAR_CHECKOUT' });
        botDispatch({ type: 'SET_SELECTIONS', selections: [], totalPrice: 0, currency: 'USD' });
        return;
      }
      const items: ItineraryItem[] = payload.itinerary_display.map((item) => ({
        selection_id: item.cart_item_id,
        product_type: item.product_type,
        display_name: item.display_name,
        display_summary: item.display_summary,
        price: item.price,
        currency: item.currency,
      }));
      botDispatch({
        type: 'SET_SELECTIONS',
        selections: items,
        totalPrice: Number(payload.total_estimated_price),
        currency: payload.currency,
      });
      botDispatch({
        type: 'SET_CHECKOUT',
        payload,
        ...(expiresAt !== undefined ? { expiresAt } : {}),
      });
    },
    [botDispatch],
  );

  const fetchCheckout = useCallback(async (sessionId: string, expiresAt?: string | null) => {
    try {
      const payload = await getCheckoutPayload(sessionId);
      applyCheckoutPayload(payload, expiresAt);
    } catch (e) {
      console.error('[BotSession] fetchCheckout', e);
    }
  }, [applyCheckoutPayload]);

  /** Load an existing session into the live chat panel. Used both on initial
   *  mount and when switching tabs. Resets the chat state so we don't leak
   *  messages from the previous session. */
  const loadSessionIntoChat = useCallback(
    async (sessionId: string): Promise<boolean> => {
      try {
        const session = await loadSession(sessionId);
        botDispatch({ type: 'RESET_BOT' });
        chatDispatch({ type: 'RESET' });
        botDispatch({ type: 'SET_SESSION', sessionId: session.id, status: session.status });
        // Before SET_MESSAGES, so the history renders in the right direction on
        // the first paint.
        botDispatch({
          type: 'SET_CONVERSATION_LANGUAGE',
          language: session.conversation_language ?? null,
        });

        const historyMsgs: UIMessage[] = (session.messages ?? [])
          .filter(m => m.role === 'user' || m.role === 'assistant')
          .map(m => ({
            id: String(m.id),
            role: m.role as 'user' | 'assistant',
            text: m.content,
            // date_created is Unix seconds from the API; the chat UI wants ISO.
            timestamp: new Date(m.date_created * 1000).toISOString(),
          }));
        chatDispatch({ type: 'SET_MESSAGES', messages: historyMsgs });

        // Cache the label for the tab strip so it survives switching away.
        // Prefer BE-derived session.title (set from first user message); fall
        // back to the first user message text in the loaded history.
        const titleSeed =
          (session.title && session.title.trim()) ||
          historyMsgs.find((m) => m.role === 'user')?.text;
        if (titleSeed) {
          botDispatch({
            type: 'SET_TAB_TITLE',
            sessionId: session.id,
            title: titleSeed.slice(0, 40),
          });
        }

        if (session.context?.current_stage) {
          chatDispatch({
            type: 'SET_CURRENT_STAGE',
            stage: session.context.current_stage as any,
          });
        }

        const lang = toBotSearchLanguage(currentLang);
        if (session.context?.search?.language !== lang) {
          await updateSessionContext(session.id, {
            search: { ...(session.context?.search ?? {}), language: lang },
          });
        }

        await refreshSelections(session.id);

        const lc = (session as any).metadata?.last_checkout;
        if (lc?.expires_at && Date.parse(lc.expires_at) > Date.now()) {
          await fetchCheckout(session.id, lc.expires_at);
        }

        // Keep the single-session-key in sync so legacy hooks that read it
        // still find the active session.
        localStorage.setItem(sKey, session.id);
        return true;
      } catch (e) {
        console.error('[BotSession] loadSessionIntoChat', e, sessionId);
        return false;
      }
    },
    [botDispatch, chatDispatch, refreshSelections, fetchCheckout, sKey, currentLang],
  );

  /** Create a fresh session row on the BE, register a tab for it, and switch
   *  to it. Returns the new session id on success.
   *
   *  This is the ONLY thing that POSTs /sessions, and it is only ever reached
   *  via ensureSession — i.e. on the user's first message. No UI action (open
   *  widget, "new chat", close tab) creates a session, so we never persist an
   *  empty "untitled" session. It deliberately does NOT reset the chat first:
   *  by the time it runs, the chat already holds the just-typed user bubble
   *  (added optimistically by ChatInput) and the prior session (if any) was
   *  already cleared by resetToEmptyChat, so a reset here would only wipe that
   *  pending bubble before we send it. */
  const createSessionInternal = useCallback(
    async (): Promise<string | null> => {
      chatDispatch({ type: 'SET_LOADING', loading: true });
      // Drop any previous failure notice so a retry doesn't render a stale error
      // alongside the in-flight attempt.
      botDispatch({ type: 'SET_SESSION_CREATE_FAILED', failed: false });
      try {
        let anonId = localStorage.getItem('sky360_bot_anon_id');
        if (!anonId) { anonId = crypto.randomUUID(); localStorage.setItem('sky360_bot_anon_id', anonId); }

        const decodeAgentIdFromJwt = (): number | undefined => {
          try {
            const token = localStorage.getItem('agent-admin-token');
            if (!token) return undefined;
            const payloadB64 = token.split('.')[1];
            if (!payloadB64) return undefined;
            const padded = payloadB64.padEnd(payloadB64.length + ((4 - (payloadB64.length % 4)) % 4), '=');
            const json = atob(padded.replace(/-/g, '+').replace(/_/g, '/'));
            const payload = JSON.parse(json);
            const id = Number(payload?.agent_id);
            return Number.isFinite(id) && id > 0 ? id : undefined;
          } catch {
            return undefined;
          }
        };
        const agentUserId = decodeAgentIdFromJwt();

        const session = await createSession({
          app: 'website',
          website_id: websiteId ? Number(websiteId) : undefined,
          agent_user_id: agentUserId,
          membership_user_id: membershipUser?.id,
          anonymous_id: agentUserId || membershipUser ? undefined : anonId,
        });

        botDispatch({ type: 'SET_SESSION', sessionId: session.id, status: session.status });
        localStorage.setItem(sKey, session.id);

        const lang = toBotSearchLanguage(currentLang);
        await updateSessionContext(session.id, {
          search: { language: lang },
          ...(membershipUser
            ? { user_profile: { name: membershipUser.name, email: membershipUser.email } }
            : {}),
        });

        setTabs(prev => [...prev, { id: session.id, title: session.title }]);
        setActiveTabId(session.id);
        return session.id;
      } catch (e) {
        console.error('[BotSession] createSessionInternal', e);
        // The send never left the client, so take back the optimistic user
        // bubble ChatInput already appended and hand the text back to the
        // composer — otherwise the message hangs in the transcript forever with
        // no reply and no explanation.
        chatDispatch({ type: 'REVERT_PENDING_SEND' });
        if (e instanceof BotNewSessionLimitError) {
          // Per-IP quota: show a countdown and disable creation until it lapses.
          botDispatch({ type: 'SET_NEW_SESSION_COOLDOWN', until: e.retryAt });
        } else {
          botDispatch({ type: 'SET_SESSION_CREATE_FAILED', failed: true });
        }
        return null;
      } finally {
        chatDispatch({ type: 'SET_LOADING', loading: false });
      }
    },
    [sKey, websiteId, membershipUser, currentLang, botDispatch, chatDispatch],
  );

  /** Drop the live chat back to the empty, session-less greeting WITHOUT
   *  creating a session. This is what "new chat" and closing the last tab do
   *  now — the session is (re)created lazily on the next message. Existing tabs
   *  are left untouched (the caller manages the tab list); only the active,
   *  live chat is cleared. */
  const resetToEmptyChat = useCallback(() => {
    // The socket stays connected (other tabs share it), so a turn still running
    // for the chat we're leaving would stream its reply straight into this fresh
    // empty chat. Stop it at the source before clearing.
    abandon(liveSessionRef.current);
    botDispatch({ type: 'RESET_BOT' });   // sessionId → null, selections/checkout cleared (tabTitles kept)
    chatDispatch({ type: 'RESET' });      // messages cleared → empty greeting (isOpen/socketStatus kept)
    localStorage.removeItem(sKey);        // drop the legacy single-session pointer
    setActiveTabId(null);
  }, [abandon, botDispatch, chatDispatch, sKey]);

  /** Lazily create the session for the *current* (empty) chat, on demand.
   *  Used by the widgets' send handler so that opening the widget — or starting
   *  a new chat — no longer persists an empty "untitled" session; the row is
   *  created only when the user actually sends their first message. Returns the
   *  existing session id if one already exists, and de-dupes concurrent
   *  first-sends so a fast double-send can't spawn two sessions. */
  const ensureSession = useCallback((): Promise<string | null> => {
    if (botState.sessionId) return Promise.resolve(botState.sessionId);
    // Cooldown still running — the POST is guaranteed to be rejected, so don't
    // spend a request rediscovering that. The composer is already disabled and
    // the notice is already on screen; this only backstops non-composer send
    // paths (card selections, programmatic sends).
    if (botState.newSessionCooldownUntil && botState.newSessionCooldownUntil > Date.now()) {
      chatDispatch({ type: 'REVERT_PENDING_SEND' });
      return Promise.resolve(null);
    }
    if (creatingRef.current) return creatingRef.current;
    const p = createSessionInternal().finally(() => {
      creatingRef.current = null;
    });
    creatingRef.current = p;
    return p;
  }, [
    botState.sessionId,
    botState.newSessionCooldownUntil,
    createSessionInternal,
    chatDispatch,
  ]);

  /** Mount-time bootstrap: rehydrate tabs from localStorage, validate active
   *  session loads, fall back to creating a fresh tab if everything is
   *  empty / stale. */
  const initSession = useCallback(async (): Promise<string | null> => {
    chatDispatch({ type: 'SET_LOADING', loading: true });
    try {
      const persisted = readPersistedTabs(tKey);
      // Legacy fallback: if we have no tabs list but the old single-session
      // key has a session id, seed the tab strip from it.
      if (persisted.tabs.length === 0) {
        const legacyId = localStorage.getItem(sKey);
        if (legacyId) {
          persisted.tabs = [{ id: legacyId }];
          persisted.activeId = legacyId;
        }
      }

      if (persisted.tabs.length > 0) {
        const targetId =
          persisted.activeId && persisted.tabs.some(t => t.id === persisted.activeId)
            ? persisted.activeId
            : persisted.tabs[0].id;

        const ok = await loadSessionIntoChat(targetId);
        if (ok) {
          setTabs(persisted.tabs);
          setActiveTabId(targetId);
          return targetId;
        }
        // Active tab failed to load — drop it and try the next, or fall
        // through to creating a fresh one.
        const remaining = persisted.tabs.filter(t => t.id !== targetId);
        if (remaining.length > 0) {
          const nextId = remaining[0].id;
          const ok2 = await loadSessionIntoChat(nextId);
          if (ok2) {
            setTabs(remaining);
            setActiveTabId(nextId);
            return nextId;
          }
        }
        // Everything stale: wipe and start fresh.
        localStorage.removeItem(tKey);
        localStorage.removeItem(sKey);
      }
    } catch (e) {
      console.error('[BotSession] initSession', e);
    } finally {
      chatDispatch({ type: 'SET_LOADING', loading: false });
    }

    // No usable persisted session. Do NOT create one here — that would persist
    // an empty "untitled" session just for opening the widget. The session is
    // created lazily on the user's first message (see ensureSession). Returning
    // null leaves the widget in its (hardcoded, FE-only) empty greeting state.
    return null;
  }, [tKey, sKey, chatDispatch, loadSessionIntoChat]);

  /** Switch to an already-open tab. */
  const switchTab = useCallback(async (sessionId: string) => {
    if (sessionId === activeTabId) return;
    // Same shared-socket hazard as resetToEmptyChat: a turn still running for
    // the tab we're leaving must not bleed into the one we're opening.
    abandon(liveSessionRef.current);
    chatDispatch({ type: 'SET_LOADING', loading: true });
    try {
      const ok = await loadSessionIntoChat(sessionId);
      if (ok) setActiveTabId(sessionId);
    } finally {
      chatDispatch({ type: 'SET_LOADING', loading: false });
    }
  }, [abandon, activeTabId, chatDispatch, loadSessionIntoChat]);

  /** Close a tab. Archives its session and falls back to a sibling tab; if the
   *  closed tab was the only one, drop to the empty session-less greeting
   *  (NOT a freshly-created session — that used to POST an empty "untitled"
   *  session just for closing a tab). */
  const closeTab = useCallback(async (sessionId: string) => {
    // Kill any turn still running for this session BEFORE the await — archiving
    // is a round trip, and until the turn is cancelled its reply is still on its
    // way to a chat the user has just dismissed.
    abandon(sessionId);
    try { await archiveSession(sessionId); } catch { /* idempotent */ }

    const nextTabs = tabs.filter(t => t.id !== sessionId);
    setTabs(nextTabs);
    botDispatch({ type: 'CLEAR_TAB_TITLE', sessionId });

    if (sessionId === activeTabId) {
      if (nextTabs.length > 0) {
        await switchTab(nextTabs[nextTabs.length - 1].id);
      } else {
        // Last tab gone — show the empty greeting; the next message recreates
        // a session lazily.
        resetToEmptyChat();
      }
    }
  }, [abandon, tabs, activeTabId, switchTab, resetToEmptyChat, botDispatch]);

  /** "New chat" — open a fresh, empty, session-less chat. The session is
   *  created lazily on the first message (see ensureSession), so clicking it
   *  no longer POSTs an empty session. Existing tabs are kept; the new chat
   *  gets its own tab only once its session is created. */
  const startNewChat = useCallback(async (): Promise<string | null> => {
    resetToEmptyChat();
    return null;
  }, [resetToEmptyChat]);

  return {
    initSession,
    ensureSession,
    startNewChat,
    refreshSelections,
    fetchCheckout,
    applyCheckoutPayload,
    // Tab API
    tabs,
    activeTabId,
    switchTab,
    closeTab,
  };
}
